import { useState, useEffect } from 'react'
import { Mail, RefreshCw, CheckCircle, LogOut } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'

export default function VerifyEmail() {
  const { user, resendVerificationEmail, refreshUser, logout } = useAuth()
  const [resending, setResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Check verification status every 5 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        await refreshUser()
      } catch (err) {
        console.error('Error refreshing user:', err)
      }
    }, 5000)

    return () => clearInterval(interval)
  }, [refreshUser])

  const handleResend = async () => {
    setResending(true)
    setError(null)
    setResendSuccess(false)

    try {
      await resendVerificationEmail()
      setResendSuccess(true)
    } catch (err) {
      setError('Failed to resend verification email. Please try again.')
      console.error('Error resending verification:', err)
    } finally {
      setResending(false)
    }
  }

  const handleCheckNow = async () => {
    setChecking(true)
    try {
      await refreshUser()
    } catch (err) {
      console.error('Error checking verification:', err)
    } finally {
      setChecking(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Mail className="w-8 h-8 text-primary-600" />
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Verify your email
        </h1>

        <p className="text-gray-600 mb-6">
          We've sent a verification email to{' '}
          <span className="font-medium text-gray-900">{user?.email}</span>.
          Please click the link in the email to verify your account.
        </p>

        {resendSuccess && (
          <div className="flex items-center gap-2 justify-center text-green-600 bg-green-50 p-3 rounded-lg mb-4">
            <CheckCircle size={18} />
            <span>Verification email sent!</span>
          </div>
        )}

        {error && (
          <div className="text-red-600 bg-red-50 p-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleCheckNow}
            disabled={checking}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {checking ? (
              <RefreshCw size={18} className="animate-spin" />
            ) : (
              <RefreshCw size={18} />
            )}
            {checking ? 'Checking...' : "I've verified my email"}
          </button>

          <button
            onClick={handleResend}
            disabled={resending}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            {resending ? (
              <RefreshCw size={18} className="animate-spin" />
            ) : (
              <Mail size={18} />
            )}
            {resending ? 'Sending...' : 'Resend verification email'}
          </button>

          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <LogOut size={18} />
            Sign out
          </button>
        </div>

        <p className="text-sm text-gray-500 mt-6">
          Didn't receive the email? Check your spam folder or try resending.
        </p>
      </div>
    </div>
  )
}
